import { Type, type Static } from "@sinclair/typebox";

const OperationId = Type.String({ format: "uuid" });
const NullableText = Type.Union([Type.String(), Type.Null()]);
const NullableUuid = Type.Union([Type.String({ format: "uuid" }), Type.Null()]);

export const PersonalJobCreateSchema = Type.Object(
  {
    operationId: OperationId,
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    customerName: Type.String({ minLength: 1, maxLength: 240 }),
    customerTaxCode: Type.Optional(NullableText),
    customerVatNumber: Type.Optional(NullableText),
    title: Type.String({ minLength: 1, maxLength: 240 }),
    description: Type.Optional(NullableText),
    openedOn: Type.String({ format: "date" }),
    hourlyRate: Type.Optional(
      Type.Union([Type.Number({ minimum: 0 }), Type.Null()]),
    ),
  },
  { additionalProperties: false },
);

export const PersonalTimeEntryCreateSchema = Type.Object(
  {
    operationId: OperationId,
    workedOn: Type.String({ format: "date" }),
    hours: Type.Number({ exclusiveMinimum: 0, maximum: 24 }),
    billingRate: Type.Optional(Type.Number({ minimum: 0 })),
    activity: Type.Optional(NullableText),
  },
  { additionalProperties: false },
);

export const PersonalCemTimeAssignmentSchema = Type.Object(
  {
    operationId: OperationId,
    personalJobId: NullableUuid,
    billingRate: Type.Optional(
      Type.Union([Type.Number({ minimum: 0 }), Type.Null()]),
    ),
  },
  { additionalProperties: false },
);

export type PersonalJobCreate = Static<typeof PersonalJobCreateSchema>;
export type PersonalTimeEntryCreate = Static<
  typeof PersonalTimeEntryCreateSchema
>;
export type PersonalCemTimeAssignment = Static<
  typeof PersonalCemTimeAssignmentSchema
>;

export interface PersonalProfile {
  id: string;
  displayName: string;
  effectiveFrom: string;
  defaultHourlyRate: string | null;
}

export interface PersonalCemTimeEntry {
  id: string;
  sourceJobId: string;
  sourceJobCode: string;
  sourceJobTitle: string;
  workedOn: string;
  hours: string;
  activity: string | null;
  personalJobId: string | null;
  billingRate: string | null;
  billableAmount: string;
  billingStatus: "pending" | "reserved" | "invoiced" | "excluded";
}

export interface PersonalJobSummary {
  id: string;
  code: string;
  managementYear: number;
  customerName: string;
  title: string;
  description: string | null;
  openedOn: string;
  status:
    | "draft"
    | "open"
    | "to-invoice"
    | "invoiced"
    | "paid"
    | "closed"
    | "archived"
    | "cancelled";
  hourlyRate: string | null;
  directHours: string;
  cemHours: string;
  totalHours: string;
  billableAmount: string;
  version: number;
}

export interface PersonalOverview {
  profile: PersonalProfile;
  fic: {
    configured: false;
    channel: "personal-independent";
    message: string;
  };
  totals: {
    pendingHours: string;
    pendingAmount: string;
    personalJobs: number;
  };
  cemTimeEntries: PersonalCemTimeEntry[];
  jobs: PersonalJobSummary[];
}
